import React, { useEffect, useState } from 'react';
import { Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import Login from './components/login/login';
import { isOvum, localStorageUpdateAfterLogin } from './utils/utils';
import Home from './components/home/home';
import Chat from './components/chat';
import Setting from './components/settings';
import { useAuthContext } from './context/AuthContext';
import { useDispatch } from 'react-redux';
import { openSideBar } from './store/sidebarSlice';
import Signup from './components/signup/Signup';
import { SESSION_TIMEOUT_DAY } from './../src/utils/constants';
import Insurance from './components/insurance';
import Sandravoice from './components/sandravoice';
import SandraWithPipecatCloud from './components/sandraVoiceWithPipecatcloud/SandraWithPipecatCloud';
import AudioGenerator from './components/sandravoice/audiogenerator/AudioGenerator';
import SophiaWithPhone from './components/sophiaWithPhone/sophiaDialOut';
import { getFeaturesForCustomer, Features } from './utils/ClientBasedFeature'
import LinkPayment from './components/linkpayment/LinkPayment';
import PaymentHistory from './components/paymenthistory/PaymentHistory';
import OvumLandingPage from './components/home/OvumLandingPage';
import MyBills from './components/bills';
import Statement from './components/bills/statement/Statement';
import ErrorPage from './components/shared/ErrorPage';
import NotFoundPage from './components/shared/NotFoundPage';
import { loginApi } from './utils/apis';
import { useTheme } from './context/ThemeContext';
import CortexDialOut from './components/cortexDialOut/CortexDialOut';
import Subscriptions from './components/subscriptions/Subscriptions';
import Donate from './components/linkpayment/donate';
import { AxisSchedulingRouter } from './components/axisScheduling';
import MeditechSchedulingDemo from './components/meditechSchedulingDemo/MeditechSchedulingDemo';

type AppRouterType = {};

const AppRoutes: React.FC<AppRouterType> = () => {
  const { setIsAuthenticated, isAuthenticated } = useAuthContext();
  const [alertMessage, setAlertMessage] = useState(null);
  const [loading, setLoading ]= useState(false);
  const [features, setFeatures] = useState<Features | undefined>(undefined);
  const { theme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const dispatch = useDispatch();

  // Check if current domain is axis
  const isAxisDomain = () => {
    return window.location.hostname.includes('axis');
  };

  const login = async (username: string, password: string) => {
    if (username !== '' && password !== '') {
      try{
        setLoading(true);
        const response = await loginApi(username, password);
        localStorageUpdateAfterLogin(response);
        dispatch(openSideBar());
        setIsAuthenticated(true);
        
        // Check if there's a redirect URL stored in localStorage
        const redirectUrl = localStorage.getItem('redirectAfterLogin');
        if (redirectUrl) {
          localStorage.removeItem('redirectAfterLogin'); // Clear the stored URL
          window.location.href = redirectUrl; // Redirect to the stored URL
        } else if(!isOvum()) {
          window.location.href = "/";
        }
      } catch (error: any) {
        setAlertMessage({
          type: 'error',
          text:  error?.response?.data?.message || 'Invalid username or password',
        } as any);
      } finally {
        setLoading(false);
      }
    } else {
      alert('username or password should not empty');
    }
  };

  useEffect(() => {
    const isAuth = localStorage.getItem('isAuthenticated') || isAuthenticated;
    const authTimestamp = localStorage.getItem('authTimestamp');
    const currentTime = new Date().getTime();

    if (
      isAuth === 'true' &&
      authTimestamp &&
      currentTime - parseInt(authTimestamp) < SESSION_TIMEOUT_DAY
    ) {
      setIsAuthenticated(true);
    } else {
      localStorage.removeItem('isAuthenticated');
      localStorage.removeItem('authTimestamp');
      // dispatch(hideSideBar())
      // navigate('/login');
    }
    const customerFeatures = getFeaturesForCustomer();
    setFeatures(customerFeatures);
  }, [isAuthenticated, setIsAuthenticated]);

   // Handle axis domain - show only axis scheduling
  if (isAxisDomain()) {
    return (
      <Routes>
        <Route path="/spravato/provider/:providerIdentifier" element={<AxisSchedulingRouter />} />
        <Route path="/spravato" element={<AxisSchedulingRouter />} />
        <Route path="/provider/:providerIdentifier" element={<AxisSchedulingRouter />} />
        <Route path="/*" element={<AxisSchedulingRouter />} />
      </Routes>
    );
  }

  if(theme.onlyPaymentsPathAllowed) {
    return (
      <Routes>
        <Route path="/donate" element={<Donate />} />
        <Route path="/payments" element={<LinkPayment />} />
        <Route path="/login" element={<Login login={login} alertMessage={alertMessage} setAlertMessage={setAlertMessage} loading={loading} />} />
        <Route path="/payment-history" element={<PaymentHistory />} />
        <Route path="/subscriptions" element={<Subscriptions />} />
        <Route path="/" element={<LinkPayment />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/error" element={<ErrorPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    )
  }

  
  if(theme.allowedPaths[0] !== "/" && theme.allowedPaths.includes(window.location.pathname)) {
    return (
      <Routes>
        <Route path="/payments" element={<LinkPayment />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    )
  }

  if(isOvum() && ["/asksandra","/insurance"].includes(window.location.pathname) && !localStorage.getItem('isAuthenticated')) {
    navigate('/')
  }

  if(isOvum()) {
    return (
    <Routes>
      <Route path="/" element={<OvumLandingPage />} />
      <Route path="/asksandra" element={<Chat />} />
      <Route path="/insurance" element={<Insurance />} />
      <Route path="/payments" element={<LinkPayment />} />
      <Route path="/payment-history" element={<PaymentHistory />} />
      <Route path="/error" element={<ErrorPage />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    );
  }
  
  if(["/","/signup","/login", "/payments", "/payment-history", "/meditech-demo"].includes(window.location.pathname) && !isAuthenticated) {
    return (
    <Routes>
      <Route path="/" element={<Home isAuthenticated={isAuthenticated} />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/login" element={<Login login={login} alertMessage={alertMessage} setAlertMessage={setAlertMessage} loading={loading} />} />
      <Route path="/meditech-demo" element={<MeditechSchedulingDemo />} />
      <Route path="/payments" element={<LinkPayment />} />
      <Route path="/payment-history" element={<PaymentHistory />} />
      <Route path="/subscriptions" element={<Subscriptions />} />
      <Route path="/error" element={<ErrorPage />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    );
  }

  if(!isAuthenticated && (window.location.pathname !== "/.well-known/apple-developer-merchantid-domain-association" && window.location.pathname !== "/.well-known/apple-developer-merchantid-domain-association/")) {
    return <Login login={login} alertMessage={alertMessage} setAlertMessage={setAlertMessage} loading={loading}/>
  }

  return (
    <Routes>
      <Route path="/" element={<Home isAuthenticated={isAuthenticated} />} />
      <Route path="/login" element={<Login login={login} alertMessage={alertMessage} setAlertMessage={setAlertMessage} loading={loading} />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/meditech-demo" element={<MeditechSchedulingDemo />} />
      <Route path="/about" element={<>Hello About us</>} />
      <Route path="/contact" element={<>Hello contact us</>} />
      <Route path="/asksandra" element={<Chat />} />
      <Route path="/setting" element={<Setting />} />
      <Route path="/insurance" element={<Insurance />} />
      <Route path="/ovumlanding" element={<OvumLandingPage />} />
      <Route path="/statement" element={<Statement />} />
      <Route path="/payment-history" element={<PaymentHistory />} />
      <Route path="/error" element={<ErrorPage />} />
      {features && features.sandraVoice && (<Route path="/sandravoice" element={<Sandravoice />} /> )}
      {features && features.sandraVoice && (<Route path="/sandravoiceV2" element={<SandraWithPipecatCloud />} /> )}
      {features && features.sandraVoice && (<Route path="/generate_audio" element={<AudioGenerator />} /> )}
      {features && features.sandraVoice && (<Route path="/sophia" element={<Sandravoice />} /> )}
      {features && features.sandraVoice && (<Route path="/sophiaWithPhone" element={<SophiaWithPhone />} /> )}
      {features && features.sandraVoice && (<Route path="/customDialOut" element={<CortexDialOut />} /> )}
      { features && features.showBillPaymentOption && <Route path="/bills" element={<MyBills />} /> }
      <Route path="/subscriptions" element={<Subscriptions />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};

export default AppRoutes;